import React, { useEffect, useState } from 'react';

import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	List,
	MenuItem,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MainGrid from '../../components/MainGrid';
import Dropzone from 'react-dropzone';
import {
	getAccessToken,
	getElderHomeId,
	getUserId,
} from '../../Utils/AuthUtil';
import { toast } from 'react-toastify';
import { DialogTitleStyle, helperText } from '../../assets/theme/Theme';
import { Close, DeleteOutline, Visibility } from '@mui/icons-material';
import ToastBanner from '../../components/ToastBanner';
import { dark, surface, teal } from '../../assets/theme/Colors';
import {
	deleteTemplate,
	fetchCareHomeConfigs,
	getCareHomeTemplates,
	storeTemplate,
	updateTemplate,
} from '../../services/UserService';
import { extractTemplate } from '../../services/CarePlanServices';
import { CloseIcon } from '../../assets/icons/Icons';

const tableHeadStyle = {
	fontSize: '11px',
	fontWeight: 600,
	color: '#64748b',
	borderBottom: '1px solid #e2e8f0',
};

const labelStyle = {
	fontSize: '12px',
	fontWeight: 500,
	color: '#6b8888',
	mb: 0.8,
};

const inputStyle = {
	'& .MuiOutlinedInput-root': {
		borderRadius: '12px',
		fontSize: '13px',
		backgroundColor: '#fff',
	},
};

const actionBtn = {
	minWidth: 34,
	width: 34,
	height: 34,
	borderRadius: '10px',
	borderColor: '#dbe3ec',
	color: '#475569',
	p: 0,
};

const TABS = {
	ptc: 'Care Plan',
	stc: 'Summarizer',
	cqc: 'CQC Report',
};

const SUMMARY_SCOPE = {
	og: 'Organization',
	re: 'Resident',
};

function TemplateForm({
	title,
	onExtractTemplate,
	buttonName,
	fileData,
	readTextFile,
	txtError,
	name,
	setName,
	nameError,
	tempTypeError,
	tempType,
	summaryTempScope,
	setSummaryTempScope,
	setTempType,
	isEditing = false,
	onClose,
	isLoading,
}) {
	return (
		<Card
			sx={{
				borderRadius: '20px',
				border: '1px solid #e2e8f0',
				boxShadow: 'none',
				width: '100%',
				backgroundColor: '#fff',
			}}>
			<CardContent sx={{ p: 3 }}>
				{/* Title */}
				<Stack
					direction="row"
					spacing={1.2}
					alignItems="center"
					mb={2.5}>
					<UploadFileOutlinedIcon
						sx={{
							fontSize: 16,
							color: teal,
						}}
					/>

					<Typography
						sx={{
							fontSize: '13px',
							fontWeight: 600,
							color: '#0f172a',
						}}>
						{title}
					</Typography>
				</Stack>

				{/* Upload Area */}
				<Dropzone onDrop={acceptedFiles => readTextFile(acceptedFiles)}>
					{({ getRootProps, getInputProps, isDragActive }) => (
						<Box
							{...getRootProps()}
							sx={{
								border: `2px dashed ${txtError?.status ? 'red' : '#b3ede8'}`,
								borderRadius: '12px',
								p: '28px 20px',
								textAlign: 'center',
								background: isDragActive
									? '#d0f5f1'
									: '#e6f9f7',
								cursor: 'pointer',
								transition: 'all 0.15s ease',
								'&:hover': {
									background: '#d0f5f1',
									borderColor: teal,
								},
							}}>
							<input {...getInputProps()} />

							<UploadFileIcon
								sx={{
									fontSize: 32,
									color: teal,
									mb: 1,
								}}
							/>
							{!fileData ? (
								<>
									<Typography
										sx={{
											fontSize: 14,
											fontWeight: 600,
											color: dark,
											mb: 0.5,
										}}>
										Upload template file
									</Typography>

									<Typography
										sx={{
											fontSize: 12,
											color: '#6b8888',
											lineHeight: 1.5,
										}}>
										Upload Care Plan and Summarizer files in
										.pdf, .docx, or .txt format.
									</Typography>
								</>
							) : (
								<Typography
									sx={{
										fontSize: 14,
										fontWeight: 600,
										color: dark,
										mb: 0.5,
									}}>
									{fileData.name}
								</Typography>
							)}
						</Box>
					)}
				</Dropzone>
				{txtError?.status && (
					<Typography style={helperText}>{txtError?.msg}</Typography>
				)}

				<Stack spacing={2} marginTop={2}>
					<Box>
						<Typography sx={labelStyle}>Template Name</Typography>

						<TextField
							fullWidth
							value={name}
							onChange={setName}
							size="small"
							placeholder="e.g. SenzCare Standard Care Plan v3"
							sx={inputStyle}
							error={nameError?.status}
						/>
						{nameError?.status && (
							<Typography style={helperText}>
								{nameError?.msg}
							</Typography>
						)}
					</Box>

					{!isEditing && (
						<Box>
							<Typography sx={labelStyle}>Type</Typography>
							<TextField
								select
								fullWidth
								size="small"
								onChange={e => {
									setTempType(e.target.value);
								}}
								error={tempTypeError?.status}
								defaultValue="Care Plan"
								value={tempType}
								sx={inputStyle}>
								<MenuItem value="Care Plan">Care Plan</MenuItem>

								<MenuItem value="Summarizer">
									Summarizer
								</MenuItem>

								{/* <MenuItem value="CQC Report">
												CQC Report
											</MenuItem> */}
							</TextField>
							{tempTypeError?.status && (
								<Typography style={helperText}>
									{tempTypeError?.msg}
								</Typography>
							)}
						</Box>
					)}
					{!isEditing && tempType === 'Summarizer' && (
						<Box>
							<Typography sx={labelStyle}>
								Summarizer Template Scope
							</Typography>
							<TextField
								select
								fullWidth
								size="small"
								onChange={e => {
									setSummaryTempScope(e.target.value);
								}}
								value={summaryTempScope}
								defaultValue="Organization"
								sx={inputStyle}>
								<MenuItem value="Organization">
									Organization
								</MenuItem>
								<MenuItem value="Resident">Resident</MenuItem>
							</TextField>
						</Box>
					)}
					<Box sx={{ flexDirection: 'row' }}>
						<Button
							onClick={onExtractTemplate}
							variant="contained"
							startIcon={<UploadFileOutlinedIcon />}
							disabled={isLoading}
							sx={{
								borderRadius: '8px',
								backgroundColor: teal,
								textTransform: 'none',
								fontWeight: 600,
								fontSize: '13px',
								boxShadow: 'none',
								'&:hover': {
									backgroundColor: teal,
									boxShadow: 'none',
								},
								color: dark,
							}}>
							{buttonName}
						</Button>
						{isEditing && (
							<Button
								onClick={onClose}
								startIcon={
									<Close sx={{ width: 16, height: 16 }} />
								}
								sx={{
									backgroundColor: '#ffffff',
									color: '#1a2424',
									fontSize: '12px',
									fontWeight: 500,
									padding: '8px 16px',
									borderRadius: '8px',
									textTransform: 'none',
									border: '1px solid #d8e8e8',
									boxShadow: 'none',
									ml: 1,
									'&:hover': {
										backgroundColor: surface,
										borderColor: teal,
									},
								}}>
								Close
							</Button>
						)}
					</Box>
				</Stack>
			</CardContent>
		</Card>
	);
}

export default function TemplateConfiguration() {
	const homeId = getElderHomeId();
	const token = getAccessToken();
	const [fileData, setFileData] = useState(null);
	const [txtError, setTxtError] = useState({ status: false, msg: '' });
	const [loading, setLoading] = useState(false);
	const [templates, setTemplates] = useState([]);
	const [name, setName] = useState('');
	const [tempType, setTempType] = useState(TABS.ptc);
	const [summaryTempScope, setSummaryTempScope] = useState(SUMMARY_SCOPE.og);
	const [nameError, setNameError] = useState({ status: false, msg: '' });
	const [tempTypeError, setTempTypeError] = useState({
		status: false,
		msg: '',
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState({});
	const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editTemplate, setEditTemplate] = useState({});
	const [editFileData, setEditFileData] = useState(null);
	const [editNameError, setEditNameError] = useState({
		status: false,
		msg: '',
	});
	const [editTxtError, setEditTxtError] = useState({
		status: false,
		msg: '',
	});
	const [loadingMsg, setLoadingMsg] = useState('');
	const [openDelete, setOpenDelete] = useState(false);
	const [templateId, setTemplateId] = useState('');
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [page, setPage] = useState(0);

	useEffect(() => {
		fetchTemplateFiles();
	}, []);

	async function fetchTemplateFiles() {
		setIsFetchingTemplates(true);
		const plResponse = await getCareHomeTemplates(homeId);

		if (!plResponse.error) {
			const templates = plResponse.content.map(item => {
				const tempStyle = getTemplateStyle(
					item.templateType,
					item.templateScope,
				);

				return {
					...item,
					...tempStyle,
				};
			});

			setTemplates(templates.reverse());
		} else {
			toast.error(plResponse.message);
		}

		setIsFetchingTemplates(false);
	}

	function getTemplateStyle(type, scope) {
		if (type === TABS.ptc) {
			return {
				type: 'Care Plan',
				color: '#14b8a6',
				bg: '#ccfbf1',
			};
		} else {
			if (scope === SUMMARY_SCOPE.og) {
				return {
					type: 'Organization Summary',
					color: '#2563eb',
					bg: '#dbeafe',
				};
			} else {
				return {
					type: 'Resident Summary',
					color: '#2563eb',
					bg: '#dbeafe',
				};
			}
		}
	}

	function readTextFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setTxtError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0] && e[0].size > 5242880) {
			setTxtError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setTxtError({ status: false, msg: '' });
		setFileData(e[0]);
	}

	function readEditTextFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf'].includes(ext)) {
			setEditTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setEditTxtError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0] && e[0].size > 5242880) {
			setEditTxtError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setEditTxtError({ status: false, msg: '' });
		setEditFileData(e[0]);
	}

	function validateInputs() {
		let isValid = true;
		if (!name || name === '') {
			setNameError({ status: true, msg: 'Enter template name' });
			isValid = false;
		}

		if (!tempType || tempType === '') {
			setTempTypeError({ status: true, msg: 'Select template type' });
			isValid = false;
		}

		if (!fileData) {
			setTxtError({ status: true, msg: 'Upload the template files' });
			isValid = false;
		}

		return isValid;
	}

	async function onExtractTemplate() {
		if (!validateInputs()) {
			return;
		}

		const formData = new FormData();
		if (tempType === TABS.ptc) {
			formData.append('template_file', fileData);
			formData.append('client_id', getElderHomeId());
			formData.append('template_name', name);
		} else {
			formData.append('summary_template_file', fileData);
			formData.append('client_id', getElderHomeId());
			formData.append('name', name);
			formData.append('template_key', name);
			formData.append('selected_endpoints_summariser', []);
			formData.append(
				'template_scope',
				summaryTempScope === SUMMARY_SCOPE.og ? 'client' : 'elder',
			);
		}

		setLoadingMsg('Uploading template...');
		setLoading(true);

		const apiEndpoint =
			tempType === TABS.ptc ? 'add_care_templates' : 'summary_templates';
		const response = await extractTemplate(formData, apiEndpoint);

		if (!response.error) {
			const templateJson =
				tempType === TABS.ptc
					? response.extracted_template
					: response.template;
			if (Object.keys(templateJson).length > 0) {
				onStoreTemplate(templateJson);
			} else {
				toast.error('Received empty template data');
				setLoading(false);
			}
		} else {
			toast.error(response.message);
			setLoading(false);
		}
	}

	async function onStoreTemplate(templateJson) {
		setLoadingMsg('Storing template...');
		setLoading(true);

		const body = {
			elderHomeId: getElderHomeId(),
			adminId: getUserId(),
			templateName: name,
			templateScope: summaryTempScope,
			templateType: tempType,
			template: templateJson,
		};

		const response = await storeTemplate(body);

		if (!response.error) {
			toast.success('Template stored successfully', {
				style: {
					background: '#4F9E4E',
				},
			});
			setFileData(null);
			setName('');
			setTempType(TABS.ptc);
			fetchTemplateFiles();
		} else {
			toast.error(response.message);
		}

		setLoading(false);
		setLoadingMsg('');
	}

	async function onExtractEditingTemplate() {
		const template = templates.find(item => item.id === editTemplate.id);
		const isNameChanged =
			template?.templateName !== editTemplate.templateName;
		const shouldUploadFile = Boolean(editFileData);

		try {
			let extractedTemplate;
			if (shouldUploadFile) {
				setLoading(true);
				setLoadingMsg('Uploading template...');

				const formData = new FormData();
				if (tempType === TABS.ptc) {
					formData.append('template_file', editFileData);
					formData.append('client_id', getElderHomeId());
					formData.append('template_name', name);
				} else {
					formData.append('summary_template_file', editFileData);
					formData.append('client_id', getElderHomeId());
					formData.append('name', name);
					formData.append('template_key', name);
					formData.append('selected_endpoints_summariser', []);
					formData.append(
						'template_scope',
						summaryTempScope === SUMMARY_SCOPE.og
							? 'client'
							: 'elder',
					);
				}

				setLoadingMsg('Uploading template...');
				setLoading(true);

				const apiEndpoint =
					tempType === TABS.ptc
						? 'add_care_templates'
						: 'summary_templates';
				const response = await extractTemplate(formData, apiEndpoint);

				if (response.error) {
					toast.error(response.message);
					setLoading(false);
					return;
				}

				const templateJson =
					tempType === TABS.ptc
						? response.extracted_template
						: response.template;
				if (Object.keys(templateJson).length > 0) {
					extractedTemplate = templateJson;
				} else {
					toast.error('Received empty template data');
					setLoading(false);
				}
			}

			if (shouldUploadFile || isNameChanged) {
				setLoading(true);
				setLoadingMsg(
					shouldUploadFile
						? 'Uploading template...'
						: 'Updating template...',
				);

				onUpdateTemplate(editTemplate.id, {
					...(isNameChanged && {
						templateName: editTemplate.templateName,
					}),
					...(shouldUploadFile && {
						template: extractedTemplate,
					}),
				});
			}
		} catch (err) {
			toast.error(err.message);
			setLoading(false);
		}
	}

	async function onUpdateTemplate(id, date) {
		setLoadingMsg('Updating template...');
		setLoading(true);

		const response = await updateTemplate(id, date);

		if (!response.error) {
			toast.success('Template updated successfully', {
				style: {
					background: '#4F9E4E',
				},
			});
			setEditFileData(null);
			setEditTemplate('');
			setTempType(TABS.ptc);
			setSummaryTempScope(SUMMARY_SCOPE.og);
			fetchTemplateFiles();
			setShowEditModal(false);
		} else {
			toast.error(response.message);
		}

		setLoading(false);
		setLoadingMsg('');
	}

	async function deleteSelectedTemplate(id) {
		const response = await deleteTemplate(id);
		if (!response.error) {
			toast.success('Template removed successfully', {
				style: {
					background: '#4F9E4E',
				},
			});
			setFileData(null);
			const filterData = templates.filter(item => item.id !== id);
			setTemplates(filterData);
			setOpenDelete(false);
		} else {
			toast.error(response.message);
		}
	}

	function onEditTemplate(id) {
		const item = templates.find(item => item.id === id);

		setEditTemplate(item);
		setShowEditModal(true);
	}

	function onViewTemplate(id) {
		const item = templates.find(item => item.id === id);
		setSelectedTemplate(item);
		setIsModalOpen(true);
	}

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	return (
		<MainGrid>
			<Box width={'100%'}>
				{/* Header */}
				<Box mb={3}>
					<Typography
						sx={{
							fontSize: '22px',
							fontWeight: 600,
							color: dark,
							mb: 0.5,
						}}>
						Template Configuration
					</Typography>

					<Typography
						sx={{
							fontSize: '14px',
							color: '#64748b',
						}}>
						Configure care plan and summarizer templates to match
						your organization&apos;s standards
					</Typography>
				</Box>

				<Grid container width="100%" spacing={1}>
					{/* LEFT */}
					<Grid item xs={12} md={7}>
						<Card
							sx={{
								borderRadius: '20px',
								border: '1px solid #e2e8f0',
								boxShadow: 'none',
							}}>
							<CardContent sx={{ p: 3 }}>
								{/* Title */}
								<Stack
									direction="row"
									spacing={1.2}
									alignItems="center"
									mb={2.5}>
									<DescriptionOutlinedIcon
										sx={{
											fontSize: 16,
											color: teal,
										}}
									/>

									<Typography
										sx={{
											fontSize: '13px',
											fontWeight: 600,
											color: '#0f172a',
										}}>
										Current Templates
									</Typography>
								</Stack>

								{/* Table */}
								<TableContainer
									sx={{
										maxHeight: 420,
										overflowY: 'auto',
										borderRadius: '12px',
										border: '1px solid #f1f5f9',
										scrollbarWidth: 'none', // Firefox
										msOverflowStyle: 'none', // IE & Edge
										'&::-webkit-scrollbar': {
											display: 'none', // Chrome/Safari
										},
									}}>
									<Table stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell sx={tableHeadStyle}>
													Template Name
												</TableCell>

												<TableCell sx={tableHeadStyle}>
													Type
												</TableCell>

												<TableCell sx={tableHeadStyle}>
													Version
												</TableCell>

												<TableCell sx={tableHeadStyle}>
													Actions
												</TableCell>
											</TableRow>
										</TableHead>

										<TableBody>
											{templates.length > 0 ? (
												templates
													.slice(
														page * rowsPerPage,
														page * rowsPerPage +
															rowsPerPage,
													)
													.map((template, index) => (
														<TableRow
															key={index}
															sx={{
																'& td': {
																	borderBottom:
																		'1px solid #f1f5f9',
																},
															}}>
															<TableCell>
																<Typography
																	sx={{
																		fontSize:
																			'13px',
																		color: '#0f172a',
																	}}>
																	{
																		template.templateName
																	}
																</Typography>
															</TableCell>

															<TableCell>
																<Chip
																	label={
																		template.type
																	}
																	size="small"
																	sx={{
																		height: 26,
																		borderRadius:
																			'999px',
																		backgroundColor:
																			template.bg,
																		color: template.color,
																		fontWeight: 500,
																		fontSize:
																			'11px',
																	}}
																/>
															</TableCell>

															<TableCell>
																<Typography
																	sx={{
																		fontSize:
																			'13px',
																		color: '#334155',
																	}}>
																	{`v${template.version}.0`}
																</Typography>
															</TableCell>

															<TableCell>
																<Stack
																	direction="row"
																	spacing={1}>
																	<Button
																		onClick={() =>
																			onViewTemplate(
																				template.id,
																			)
																		}
																		variant="outlined"
																		size="small"
																		sx={
																			actionBtn
																		}>
																		<Visibility
																			sx={{
																				fontSize: 16,
																			}}
																		/>
																	</Button>
																	<Button
																		onClick={() =>
																			onEditTemplate(
																				template.id,
																			)
																		}
																		variant="outlined"
																		size="small"
																		sx={
																			actionBtn
																		}>
																		<EditOutlinedIcon
																			sx={{
																				fontSize: 16,
																			}}
																		/>
																	</Button>
																	<Button
																		onClick={() => {
																			setTemplateId(
																				template.id,
																			);
																			setOpenDelete(
																				true,
																			);
																		}}
																		variant="outlined"
																		size="small"
																		sx={
																			actionBtn
																		}>
																		<DeleteOutline
																			sx={{
																				fontSize: 16,
																			}}
																		/>
																	</Button>
																</Stack>
															</TableCell>
														</TableRow>
													))
											) : (
												<TableRow>
													<TableCell
														colSpan={4}
														align="center">
														<Typography
															sx={{
																fontSize: 13,
															}}>
															No data available
														</Typography>
													</TableCell>
												</TableRow>
											)}
										</TableBody>
									</Table>
								</TableContainer>
								<TablePagination
									rowsPerPageOptions={[]}
									component="div"
									count={templates ? templates.length : 0}
									rowsPerPage={rowsPerPage}
									page={page}
									onPageChange={handleChangePage}
									onRowsPerPageChange={
										handleChangeRowsPerPage
									}
								/>
							</CardContent>
						</Card>
					</Grid>

					{/* RIGHT */}
					<Grid item xs={12} md={5}>
						<TemplateForm
							title={'Upload Custom Template'}
							readTextFile={readTextFile}
							txtError={txtError}
							fileData={fileData}
							name={name}
							setName={e => {
								setName(e.target.value);
								setNameError({
									status: false,
									msg: '',
								});
							}}
							nameError={nameError}
							setTempType={setTempType}
							tempTypeError={tempTypeError}
							tempType={tempType}
							setSummaryTempScope={setSummaryTempScope}
							onExtractTemplate={onExtractTemplate}
							buttonName="Upload & Activate"
							isLoading={loading}
							summaryTempScope={summaryTempScope}
						/>
					</Grid>
				</Grid>
			</Box>
			<Dialog
				open={showEditModal}
				fullWidth
				maxWidth="lg"
				onClose={() => setShowEditModal(false)}
				scroll="body"
				PaperProps={{
					style: {
						backgroundColor: 'transparent',
						boxShadow: 'none',
					},
				}}>
				<DialogContent
					style={{
						padding: '0px 0px 20px 0px',
					}}>
					<Grid container padding={'10px'}>
						<TemplateForm
							isEditing={true}
							title={'Update Custom Template'}
							readTextFile={readEditTextFile}
							txtError={editTxtError}
							fileData={editFileData}
							name={editTemplate.templateName}
							setName={e => {
								setEditTemplate(prevState => ({
									...prevState,
									templateName: e.target.value,
								}));
								setEditNameError({
									status: false,
									msg: '',
								});
							}}
							nameError={editNameError}
							onExtractTemplate={onExtractEditingTemplate}
							buttonName="Update Template"
							onClose={() => setShowEditModal(false)}
							isLoading={loading}
							summaryTempScope={summaryTempScope}
						/>
					</Grid>
				</DialogContent>
			</Dialog>
			<Dialog
				open={isModalOpen}
				fullWidth
				maxWidth="lg"
				onClose={() => setIsModalOpen(false)}
				scroll="body"
				PaperProps={{
					style: {
						backgroundColor: 'transparent',
						boxShadow: 'none',
					},
				}}>
				<DialogContent
					style={{
						padding: '0px 0px 20px 0px',
					}}>
					<Card
						sx={{
							borderRadius: '20px',
							border: '1px solid #e2e8f0',
							boxShadow: 'none',
							width: '100%',
							backgroundColor: '#fff',
						}}>
						<CardContent sx={{ p: 3 }}>
							<Grid
								container
								justifyContent={'space-between'}
								alignItems={'center'}>
								<Typography
									sx={{
										fontSize: '13px',
										fontWeight: 600,
										color: '#0f172a',
									}}>
									{selectedTemplate.templateName ??
										'Template Name'}
								</Typography>
								<IconButton
									onClick={() => setIsModalOpen(false)}>
									<CloseIcon />
								</IconButton>
							</Grid>
							<Paper sx={{ p: 2, mt: 2, boxShadow: 'none' }}>
								<List>
									{selectedTemplate.template ? (
										<pre
											style={{
												whiteSpace: 'pre-wrap',
												wordBreak: 'break-word',
												overflowWrap: 'anywhere',
												margin: 0,
											}}>
											{JSON.stringify(
												selectedTemplate.template,
												null,
												2,
											)}
										</pre>
									) : (
										<Typography>
											No data available
										</Typography>
									)}
								</List>
							</Paper>
						</CardContent>
					</Card>
				</DialogContent>
			</Dialog>
			<Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
				<DialogTitle style={DialogTitleStyle}>
					Remove Template
				</DialogTitle>

				<DialogContent
					variant="subtitle1"
					style={{
						color: 'rgba(113, 113, 113, 1)',
						fontWeight: 600,
					}}>
					<Typography sx={{ fontSize: 14 }}>
						Are you sure you want to remove this template? This
						action cannot be undone.
					</Typography>
				</DialogContent>

				<DialogActions
					style={{
						marginRight: '20px',
						marginBottom: '10px',
					}}>
					<Button
						onClick={() => setOpenDelete(false)}
						sx={{
							paddingLeft: '15px',
							paddingRight: '5px',
							color: '#28696D',
							textTransform: 'none',
							marginRight: '0px',
						}}>
						Cancel
					</Button>

					<Button
						variant="contained"
						sx={{
							backgroundColor: teal,
							boxShadow: 'none',
							marginLeft: '10px',
							textTransform: 'none',
							'&:hover': {
								backgroundColor: teal,
								boxShadow: 'none',
							},
						}}
						onClick={() => deleteSelectedTemplate(templateId)}>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
			<ToastBanner
				open={loading}
				message={loadingMsg}
				icon={<AutoAwesomeIcon color={'#FFFFFF'} />}
			/>
		</MainGrid>
	);
}
